import _ from "lodash";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Icon, Label, Button } from "semantic-ui-react";
import FileUploader from "./FileUploader";
import AppSettings from "../config";
import "./ControlledFileUploader.scss";
import { humanizeString } from "../lib/humanize-string";
import { useId } from "react-aria";

interface IControlledFileUploader {
  name: string;
  control: any;
  defaultValue: any;
  readonly?: boolean;
  required?: boolean;
  folder: string;
  onRemoved?: any;
  onUploaded?: any;
  onBlur?: any;
  singleFileOnly?: boolean;
  errors?: any;
  accept?: string;
  label?: string;
  id?: string;
  accessibilityLabel?: string;
}

export const ControlledFileUploader = ({
  name,
  control,
  onUploaded,
  onRemoved,
  readonly,
  required,
  defaultValue,
  folder,
  onBlur,
  singleFileOnly = false,
  errors,
  accept,
  label,
  id: inputId,
  // The accessibilityLabel for screen readers. Defaults to the label prop if not provided.
  accessibilityLabel = label,
}: IControlledFileUploader) => {
  const id = inputId ?? useId();
  const isErrorSelector = (errors: any) => (fieldName: string) => !!_.get(errors, `${fieldName}.message`);
  const errorMessageSelector = (errors: any) => (fieldName: string) =>
    isErrorSelector(fieldName)
      ? _.get(errors, `${fieldName}.message`).replace(fieldName, humanizeString(fieldName))
      : null;

  const isError = isErrorSelector(errors)(name);
  const [files, setFiles] = useState<any[]>(defaultValue || []);
  useEffect(() => {
    setFiles(defaultValue || []);
  }, [defaultValue]);

  // Determines if a signedUrl/signed_url field exists in a dictionary and returns their value
  const signedUrl = (dict: any) => {
    if (dict?.signed_url) return dict.signed_url;
    if (dict?.signedUrl) return dict.signedUrl;
    return "";
  };

  const reachedFileLimit = singleFileOnly && files.length >= 1;
  const uploadPrompt = required ? "Click or Drag Files to Add" : "Click or Drag Files to Add";
  const removePrompt = "Remove Attachment";

  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: required && "This field is required" }}
      required={required}
      render={({ onChange }) => {
        return (
          <>
            {label && (
              //13px font size, the default for our labels elsewhere, in rem
              <span id={!readonly ? id : undefined} className='tw-font-bold' style={{ fontSize: ".94rem" }}>
                {label}
              </span>
            )}
            {!readonly && !reachedFileLimit && (
              <FileUploader
                baseUrl={AppSettings.CONTENT_URL}
                bucket={AppSettings.DIRECT_UPLOAD_BUCKET}
                folder={folder}
                file={null}
                accept={accept}
                multiple={!singleFileOnly}
                setFile={(file: any) => {
                  setFiles((files) => {
                    onChange([...files, file]);
                    return [...files, file];
                  });
                  if (onUploaded) {
                    onUploaded(file);
                  }
                  if (onBlur) onBlur();
                }}
                uploadId='file'
                placeholder={
                  <button
                    id={id}
                    type='button'
                    className='accessible-input tw-w-full tw-cursor-pointer tw-border-none tw-bg-[#FAFBFC] tw-py-3 tw-text-center tw-outline-none tw-ring-[#7faecfbf] focus:tw-ring-1'
                    aria-describedby={`${name}-error`}
                    aria-label={accessibilityLabel ? `${accessibilityLabel} - ${uploadPrompt}` : uploadPrompt}
                  >
                    <Icon name='plus' color='grey' />
                    {uploadPrompt}
                  </button>
                }
              />
            )}
            <div className='tw-mt-2 tw-flex tw-flex-col tw-gap-2'>
              {files.map((file: any) => {
                if (readonly) {
                  return (
                    <p key={file.key} className='attachment-no-wrap'>
                      <a className='tw-text-blue' href={signedUrl(file)} target='_blank' rel='noopener noreferrer'>
                        {file.name}
                      </a>
                    </p>
                  );
                }
                return (
                  <Label className='tw-m-0' key={file.key}>
                    <p style={{ fontSize: 14, display: "flex", justifyContent: "space-between" }} key={file.key}>
                      {signedUrl(file) ? (
                        <a
                          href={signedUrl(file)}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='attachment-no-wrap'
                        >
                          {file.name}
                        </a>
                      ) : (
                        file.name
                      )}
                      <Button
                        icon='close'
                        type='button'
                        title={removePrompt}
                        className='icon-only-button'
                        aria-label={accessibilityLabel ? `${accessibilityLabel} - ${removePrompt}` : removePrompt}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setFiles((files) => {
                            const removed = files.find((f) => f.key === file.key);
                            const filesWithoutRemoved = files.filter((f) => f.key !== file.key);
                            if (onRemoved) {
                              onRemoved(removed);
                            }
                            onChange(filesWithoutRemoved);
                            return filesWithoutRemoved;
                          });
                          if (onBlur) {
                            onBlur();
                          }
                        }}
                      />
                    </p>
                  </Label>
                );
              })}
            </div>
            {isError && (
              <Label basic prompt pointing='above' aria-live='polite' id={`${name}-error`}>
                {errorMessageSelector(errors)(name)}
              </Label>
            )}
          </>
        );
      }}
    />
  );
};
